import Covid19SignedAckModal from '@app/covid19/Covid19SignedAckModal';
import CovidAckTemplateModal from '@app/covid19/CovidAckTemplateModal';
import Covid19UploadedPdfModal from '@app/covid19/Covid19UploadedPdfModal';
import { AppointmentStatus } from '@bb/domain/dist/types';
import {
  message,
  theme,
  TableColumnType,
  Skeleton,
  SkeletonAvatar,
  SkeletonButton,
} from 'components';
import {
  AuthContext,
  FeedbackModalContext,
  AppointmentActionModalContext,
} from 'contexts';
import { useTableResizer, useBreakpoint, useCovidSign, useLink } from 'hooks';
import { groupBy } from 'lodash';
import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  featureEnabled,
  formatDateStrNoOrd,
  formatDateStrWithYearShortMonthAndDayNoOrd,
  getInternalShowingDetailsUrl,
  openAppointmentModal as _openAppointmentModal,
  getLocalTimeZone,
} from 'utils';
import { zonedTimeToUtc } from 'date-fns-tz';
import { StyledTable } from '../../../components';
import { DEFAULT_STATUS_FILTERS, ES_DELAY } from '../constants';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import {
  AgentCalendarMenu,
  AppointmentTypes,
  CommonInnerTableProps,
  ListingCalendarMenu,
  OuterTableData,
  BuyerTableData,
  BuyerShowing,
} from '../types';
import { PrintButton, ShowingCalendarPagination } from './components';
import InPersonTable from './InPersonTable';
import ListingTable from './ListingTable';
import ManagedAccessTable from './ManagedAccessTable';
import VirtualTable from './VirtualTable';
import EmptyState from '../EmptyState';
import BuyerTableWrapper from './BuyerTableWrapper';
import { getTourItemDateToShow } from '../../TourEditPage/utils';

const CURRENT_YEAR = new Date().getFullYear();

const tableHasMoreThanOneTour = (showings: BuyerTableData) => {
  let numberOfTours = 0;
  const tourIdsMap = new Set();
  for (let i = 0; i < showings.length; i++) {
    const showing = showings[i];
    if (showing.tourName) {
      if (!tourIdsMap.has(showing.appointmentTourId)) {
        tourIdsMap.add(showing.appointmentTourId);
        numberOfTours += 1;
      }
    }

    if (numberOfTours > 1) {
      return true;
    }
  }

  return false;
};

const OuterTable: React.FC = () => {
  const linkTo = useLink();

  const { auth } = React.useContext(AuthContext);
  const { md } = useBreakpoint();
  const {
    appointmentList,
    selectedMenuOption,
    isFetching,
    listingId,
    statuses,
    refetchPage,
  } = useShowingsCalendarContext();
  const { openModal: openFeedbackModal } =
    React.useContext(FeedbackModalContext);
  const { openModal: openAppointmentActionModal } = React.useContext(
    AppointmentActionModalContext,
  );
  const {
    isWorking: isCovidSignWorking,
    resetCovidStates,
    selectedCovidAppt,
    setSelectedCovidAppt,
    selectedCovidTemplate,
    selectedCovidSignSettings,
    handleCovidSign,
  } = useCovidSign();

  const processedStatuses = !statuses?.length
    ? DEFAULT_STATUS_FILTERS
    : statuses;

  // Wrapper to provide necessary props
  const openAppointmentModal = (
    ...[id, _listingId, tab, options]: Parameters<typeof _openAppointmentModal>
  ) => {
    if (featureEnabled('showingDetailsRevampFeaturePhase', 3)) {
      let link = '';
      link += `${getInternalShowingDetailsUrl(
        id,
      )}?redirectTo=calendar&isListingAgent=true`;

      linkTo(link);
      return;
    }
    return _openAppointmentModal(id, _listingId, tab, {
      ...options,
      additionalProps: {
        ...options?.additionalProps,
        openAppointmentActionModal,
        onAppointmentActionModalConfirm: refetchPage,
        openFeedbackModal,
        onFeedbackModalSubmit: refetchPage,
        onCovidAckSubmit: (ackType: string) => {
          message.success(
            `COVID-19 ${ackType === 'override' ? 'overridden' : 'uploaded'}`,
          );
          refetchPage(ES_DELAY.DEFAULT);
        },
      },
    });
  };

  const groupedAppointments = groupBy(
    appointmentList,
    ({
      date,
      originalDate,
      status,
      // @ts-expect-error tourName only exists in BuyerTableData
      tourName,
      // @ts-expect-error listingContactTimeChange only exists in BuyerTableData
      listingContactTimeChange,
    }: AppointmentTypes[number]) =>
      tourName
        ? getTourItemDateToShow(
            {
              status: status as AppointmentStatus,
              listingContactTimeChange,
              date,
              originalDate,
            },
            getLocalTimeZone(auth),
          )
        : date,
  );
  const chronologicalShowingsEnabled = featureEnabled('chronologicalShowings');
  const dataSource = Object.entries(groupedAppointments)
    .map(([date, showings]) => {
      const sortedShowings = chronologicalShowingsEnabled
        ? [...showings].sort(
            (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
          )
        : showings;
      return {
        date,
        time:
          groupedAppointments[date][0].originalTime &&
          (groupedAppointments[date][0] as BuyerShowing).tourName &&
          ((groupedAppointments[date][0].status as AppointmentStatus) ===
            'suggested_time_change' ||
            (groupedAppointments[date][0].status === 'time_change' &&
              (groupedAppointments[date][0] as BuyerShowing)
                .listingContactTimeChange))
            ? groupedAppointments[date][0].originalTime
            : groupedAppointments[date][0].time,
        showings: sortedShowings,
      };
    })
    .sort((a, b) => {
      if (chronologicalShowingsEnabled) {
        return a.date <= b.date ? -1 : 1;
      }
      return a.date <= b.date ? 1 : -1;
    }) as OuterTableData[];

  const tableWrapperRef = useRef<HTMLDivElement>();
  const { tableHeight } = useTableResizer({
    tableStartSelector: `${StyledTableRow}:not([aria-hidden='true'])`,
    wrapperRef: tableWrapperRef,
  });

  const columns: TableColumnType<OuterTableData>[] = useMemo(
    () => [
      {
        key: 'date',
        dataIndex: 'time',
        render: (time, { date, showings }) =>
          new Date(time || date).getFullYear() === CURRENT_YEAR
            ? time
              ? formatDateStrNoOrd(
                  zonedTimeToUtc(
                    time,
                    (showings[0] as BuyerShowing).listing?.timezone ||
                      (showings[0] as BuyerShowing).timezone,
                  ),
                )
              : formatDateStrNoOrd(date)
            : formatDateStrWithYearShortMonthAndDayNoOrd(time || date),
      },
      {
        key: 'print',
        dataIndex: 'date',
        align: 'right',
        render: date =>
          selectedMenuOption !== AgentCalendarMenu.ManagedAccess && (
            <PrintButton
              date={date}
              listingId={listingId}
              statuses={processedStatuses}
              selectedMenuOption={selectedMenuOption}
              dataCy="showings-date-print"
            />
          ),
      },
    ],
    [listingId, selectedMenuOption, processedStatuses],
  );

  const skeletons = useMemo(
    () => (
      <>
        {[...Array(7)].map((_, index) => (
          <StyledSkeletonListItem key={`skeleton-${index}`}>
            <StyledSkeletonElement>
              <SkeletonAvatar shape="square" size={42} />
              <div>
                <SkeletonTitle type="header" />
                <SkeletonTitle />
              </div>
            </StyledSkeletonElement>
            <StyledSkeletonElement>
              <StyledSkeletonButton />
              <StyledSkeletonButton />
            </StyledSkeletonElement>
          </StyledSkeletonListItem>
        ))}
      </>
    ),
    [],
  );

  let InnerTableComponent: React.FC<CommonInnerTableProps>;
  switch (selectedMenuOption) {
    case AgentCalendarMenu.Listing: {
      InnerTableComponent = ListingTable;
      break;
    }
    case ListingCalendarMenu.InPerson: {
      InnerTableComponent = InPersonTable;
      break;
    }
    case ListingCalendarMenu.Virtual: {
      InnerTableComponent = VirtualTable;
      break;
    }
    case AgentCalendarMenu.ManagedAccess: {
      InnerTableComponent = ManagedAccessTable;
      break;
    }
    default: {
      InnerTableComponent = BuyerTableWrapper;
    }
  }

  const expandableOptions = useMemo(
    () => ({
      defaultExpandAllRows: true,
      expandedRowRender: (record: OuterTableData) => (
        <InnerTableComponent
          key={record.date}
          tableKey={record.date}
          showings={record.showings}
          selectedCovidAppt={selectedCovidAppt}
          setSelectedCovidAppt={setSelectedCovidAppt}
          openAppointmentModal={openAppointmentModal}
          isCovidSignWorking={isCovidSignWorking}
        />
      ),
      expandedRowClassName: ({ showings }: OuterTableData) =>
        // tableHastour classname should be added if showings contain tour items and non touritems
        // OR if showings contain more that one tour
        ((showings as BuyerTableData).some(({ tourName }) =>
          Boolean(tourName),
        ) &&
          (showings as BuyerTableData).some(({ tourName }) => !tourName)) ||
        tableHasMoreThanOneTour(showings as BuyerTableData)
          ? 'tableHasTour'
          : '',
    }),
    [InnerTableComponent, openAppointmentModal],
  );

  const tableComponents = useMemo(
    () => ({
      table: StyledOuterTable,
      body: {
        wrapper: (props: any) => <StyledTableBody isMobile={!md} {...props} />,
        row: StyledTableRow,
        cell: StyledTableCell,
      },
    }),
    [md],
  );

  if (isFetching) {
    return skeletons;
  }

  if (!dataSource.length) {
    return <EmptyState />;
  }

  return (
    <>
      <div ref={tableWrapperRef}>
        <StyledTable
          columns={columns}
          components={tableComponents}
          rowKey={record => record.date}
          $height={tableHeight}
          $headerHeight={0}
          showHeader={false}
          pagination={false}
          expandIconColumnIndex={-1}
          tableLayout="fixed"
          scroll={{
            x: md ? '1000px' : '100%',
            y: '100%',
          }}
          expandable={expandableOptions}
          dataSource={isFetching ? [] : dataSource}
          locale={{ emptyText: <span></span> }}
        />
        <ShowingCalendarPagination />
      </div>
      <CovidAckTemplateModal
        ackTemplate={selectedCovidTemplate}
        // @ts-expect-error Missing props from EnhancedAppointment aren't needed for the modal
        appointment={selectedCovidAppt}
        country={auth.org.country}
        onCancel={resetCovidStates}
        onSubmit={signature =>
          handleCovidSign(signature, success => {
            if (success) refetchPage(ES_DELAY.GLACIAL);
          })
        }
        visible={Boolean(selectedCovidAppt && selectedCovidTemplate)}
      />
      <Covid19SignedAckModal
        // @ts-expect-error Missing props from EnhancedAppointment aren't needed for the modal
        appointment={selectedCovidAppt}
        appointmentSignSettings={selectedCovidSignSettings}
        country={auth.org.country}
        onCancel={resetCovidStates}
        visible={Boolean(
          selectedCovidAppt && selectedCovidSignSettings?.template,
        )}
      />
      <Covid19UploadedPdfModal
        onCancel={resetCovidStates}
        PDFAckFile={selectedCovidSignSettings?.PDFAckFile}
        visible={Boolean(
          selectedCovidAppt && selectedCovidSignSettings?.PDFAckFile,
        )}
      />
    </>
  );
};

export default OuterTable;

const StyledOuterTable = styled.table`
  // For chat icon interference
  margin-bottom: 30px;
`;

const StyledTableBody = styled.tbody<{ isMobile?: boolean }>`
  & > tr.ant-table-row-level-0 > td {
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${theme.gray7};
    font-weight: 500;
    cursor: default;
  }

  & > tr.ant-table-expanded-row-level-1 > td {
    padding-left: 0;
  }
`;

const StyledTableRow = styled.tr`
  background-color: ${theme.backgroundColorLight};
  &.tableHasTour > td {
    padding: 0;
  }
`;

const SkeletonTitle = styled(props => (
  <Skeleton paragraph={false} {...props} />
))<{
  type?: 'header';
}>`
  width: ${({ type }) => (type === 'header' ? 250 : 150)}px;
  margin-left: 15px;
  & .ant-skeleton-title {
    margin: 5px;
  }
`;

const StyledSkeletonListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 21px 32px;
  box-shadow: inset 0px -1px 0px ${theme.neutral4};
`;

const StyledSkeletonElement = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSkeletonButton = styled(SkeletonButton)`
  margin: 5px;
`;

const StyledTableCell = styled.td`
  background-color: inherit;
  &&& {
    color: ${theme.gray8};
  }
`;
